/**
 * /sign-up — email + password + optional first/last name.
 *
 * Flow (see references/clerk-flows.md §Sign-up):
 *   1. `signUp.create({ emailAddress, password, firstName?, lastName? })`
 *   2. `signUp.prepareEmailAddressVerification({ strategy: 'email_code' })`
 *   3. navigate to `/verify-email` — `useSignUp()` carries the state
 *      across the navigation because Clerk's client stores it on the
 *      `Client` resource.
 *
 * Invite acceptance: this instance runs sign-up in restricted mode
 * (see /sign-in's "invitation-only" copy) — the flow above only
 * works for teammates who arrived via a Clerk organization-invite
 * link. That link appends `?__clerk_ticket=...`; when present we
 * skip the form above entirely and redeem the ticket instead (see
 * `InviteAcceptForm` / `acceptInviteTicket` in `@/lib/auth`).
 */

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Field, FieldError, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import {
  acceptInviteTicket,
  activateSession,
  completeInviteSignUp,
  signUpWithPassword,
  ticketOrganizationId,
} from '@/lib/auth';
import { LEGAL_DOCUMENTS, buildLegalAcceptance } from '@/lib/legal';
import { useSignUp } from '@clerk/react/legacy';
import { Eye, EyeSlash } from '@phosphor-icons/react';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { type FormEvent, type ReactElement, useId, useRef, useState } from 'react';

const CONSENT_REQUIRED_MESSAGE = 'Please accept the Terms and Privacy Policy to continue.';

export const Route = createFileRoute('/_auth/sign-up')({
  component: SignUpPage,
  validateSearch: (search): { ticket?: string } => ({
    ticket: typeof search.__clerk_ticket === 'string' ? search.__clerk_ticket : undefined,
  }),
});

function SignUpPage(): ReactElement {
  const { ticket } = Route.useSearch();
  return ticket ? <InviteAcceptForm ticket={ticket} /> : <SignUpForm />;
}

/**
 * The consent gate. Rendered unticked; the caller blocks submission
 * until it is checked, so an account can never be created without an
 * acceptance to record.
 *
 * Document list + versions come from `lib/legal.ts`, which derives
 * them from the shared `SIGNUP_LEGAL_DOCUMENTS` contract the webhook
 * validates against — the copy here cannot drift from what gets
 * recorded.
 */
function LegalConsentField({
  checked,
  onChange,
  error,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  error?: string;
}): ReactElement {
  const consentId = useId();
  return (
    <>
      <div className="flex items-start gap-2">
        <Checkbox
          id={consentId}
          checked={checked}
          onCheckedChange={(next) => onChange(next === true)}
          aria-invalid={error ? true : undefined}
          className="mt-0.5"
        />
        <label htmlFor={consentId} className="text-xs text-[var(--color-mute)]">
          I agree to the{' '}
          {LEGAL_DOCUMENTS.map((doc, index) => (
            <span key={doc.slug}>
              {index > 0 && (index === LEGAL_DOCUMENTS.length - 1 ? ' and ' : ', ')}
              <a
                href={doc.href}
                target="_blank"
                rel="noreferrer"
                className="font-medium text-[var(--color-accent-brand)] hover:underline"
              >
                {doc.label}
              </a>
            </span>
          ))}
          .
        </label>
      </div>
      {error && (
        <p className="-mt-2 text-xs text-[var(--color-danger)]" role="alert">
          {error}
        </p>
      )}
    </>
  );
}

function InviteAcceptForm({ ticket }: { ticket: string }): ReactElement {
  const { signUp, setActive, isLoaded } = useSignUp();
  const navigate = useNavigate();

  // Starts at `consent`, NOT at `accepting`.
  //
  // Redeeming the ticket is what creates the account, and it can return
  // `complete` outright — activating a session with no further UI. If
  // we redeemed on mount (as this did before) an invitee could land
  // fully signed-in having accepted nothing, with no screen left on
  // which to ask. So the redemption now sits behind an explicit
  // "Accept & join", and consent rides along on the same call.
  const [phase, setPhase] = useState<'consent' | 'accepting' | 'needs_password' | 'error'>(
    'consent',
  );
  const [consent, setConsent] = useState(false);
  const [inviteEmail, setInviteEmail] = useState<string | undefined>(undefined);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string | undefined>>({});
  const [formError, setFormError] = useState<string | undefined>(undefined);

  const firedRef = useRef(false);

  async function onAcceptInvite() {
    if (!isLoaded || !signUp || firedRef.current) return;
    if (!consent) {
      setFieldErrors({ consent: CONSENT_REQUIRED_MESSAGE });
      return;
    }
    firedRef.current = true;
    setFieldErrors({});
    setPhase('accepting');

    const result = await acceptInviteTicket({
      signUp,
      ticket,
      legalAcceptance: buildLegalAcceptance('invite'),
    });

    if (result.kind === 'error') {
      setFormError(result.formError ?? 'This invite link is no longer valid.');
      setPhase('error');
      return;
    }

    if (result.kind === 'needs_password') {
      setInviteEmail(result.emailAddress);
      setPhase('needs_password');
      return;
    }

    if (!setActive) return;
    await activateSession({
      setActive,
      sessionId: result.sessionId,
      redirectUrl: '/',
      organization: ticketOrganizationId(ticket),
    });
  }

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!isLoaded || !signUp || !setActive) return;

    if (password !== confirm) {
      setFieldErrors({ confirm: 'Passwords do not match.' });
      return;
    }

    setSubmitting(true);
    setFieldErrors({});
    setFormError(undefined);

    const result = await completeInviteSignUp({ signUp, password });

    if (result.kind === 'error') {
      setFieldErrors(result.fieldErrors);
      setFormError(result.formError);
      setSubmitting(false);
      return;
    }

    if (result.kind === 'complete') {
      await activateSession({
        setActive,
        sessionId: result.sessionId,
        redirectUrl: '/',
        organization: ticketOrganizationId(ticket),
      });
      return;
    }

    setFormError('Unexpected state. Try again.');
    setSubmitting(false);
  }

  if (phase === 'consent') {
    return (
      <div className="space-y-6">
        <header className="space-y-1 text-center">
          <h1 className="text-xl font-semibold text-[var(--color-ink)]">You've been invited</h1>
          <p className="text-sm text-[var(--color-mute)]">
            Join your team on Clickweave. Accept the terms below to continue.
          </p>
        </header>

        {formError && (
          <div
            className="rounded-md border border-[var(--color-danger)]/30 bg-[var(--color-danger)]/10 px-3 py-2 text-sm text-[var(--color-danger)]"
            role="alert"
          >
            {formError}
          </div>
        )}

        <div className="space-y-4">
          <LegalConsentField
            checked={consent}
            onChange={(next) => {
              setConsent(next);
              if (next) setFieldErrors({});
            }}
            error={fieldErrors.consent}
          />

          {/* Clerk may run bot protection on the ticket redemption. */}
          <div id="clerk-captcha" />

          <Button className="w-full" disabled={!isLoaded || !consent} onClick={onAcceptInvite}>
            Accept &amp; join
          </Button>
        </div>

        <p className="text-center text-sm text-[var(--color-mute)]">
          Wrong account?{' '}
          <button
            type="button"
            onClick={() => navigate({ to: '/sign-in' })}
            className="font-medium text-[var(--color-accent-brand)] hover:underline"
          >
            Sign in instead
          </button>
        </p>
      </div>
    );
  }

  if (phase === 'accepting') {
    return (
      <div className="flex flex-col items-center justify-center gap-4 p-6 text-center">
        <Spinner className="size-5 text-[var(--color-mute)]" />
        <p className="text-sm text-[var(--color-mute)]">Accepting your invite…</p>
      </div>
    );
  }

  if (phase === 'error') {
    return (
      <div className="space-y-4 text-center">
        <h1 className="text-xl font-semibold text-[var(--color-ink)]">Invite link expired</h1>
        <p className="text-sm text-[var(--color-mute)]">{formError}</p>
        <Link
          to="/sign-in"
          className="font-medium text-[var(--color-accent-brand)] hover:underline"
        >
          Back to sign in
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <header className="space-y-1 text-center">
        <h1 className="text-xl font-semibold text-[var(--color-ink)]">
          {inviteEmail ? `Welcome, ${inviteEmail}` : 'Welcome'}
        </h1>
        <p className="text-sm text-[var(--color-mute)]">
          Set a password to finish joining your team.
        </p>
      </header>

      <form className="space-y-4" onSubmit={onSubmit} noValidate>
        <Field>
          <FieldLabel htmlFor="invite-password">Password</FieldLabel>
          <div className="relative w-full">
            <Input
              id="invite-password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              required
              minLength={8}
              value={password}
              onChange={(event) => setPassword((event.target as HTMLInputElement).value)}
              aria-invalid={fieldErrors.password ? true : undefined}
            />
            <button
              type="button"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              onClick={() => setShowPassword((prev) => !prev)}
              className="absolute inset-y-0 right-2 flex items-center text-[var(--color-mute)] hover:text-[var(--color-ink)]"
            >
              {showPassword ? (
                <EyeSlash aria-hidden="true" size={16} />
              ) : (
                <Eye aria-hidden="true" size={16} />
              )}
            </button>
          </div>
          {fieldErrors.password ? (
            <FieldError>{fieldErrors.password}</FieldError>
          ) : (
            <p className="text-xs text-[var(--color-mute)]">At least 8 characters.</p>
          )}
        </Field>

        <Field>
          <FieldLabel htmlFor="invite-confirm">Confirm password</FieldLabel>
          <Input
            id="invite-confirm"
            type={showPassword ? 'text' : 'password'}
            autoComplete="new-password"
            required
            value={confirm}
            onChange={(event) => setConfirm((event.target as HTMLInputElement).value)}
            aria-invalid={fieldErrors.confirm ? true : undefined}
          />
          {fieldErrors.confirm && <FieldError>{fieldErrors.confirm}</FieldError>}
        </Field>

        {formError && (
          <div
            className="rounded-md border border-[var(--color-danger)]/30 bg-[var(--color-danger)]/10 px-3 py-2 text-sm text-[var(--color-danger)]"
            role="alert"
          >
            {formError}
          </div>
        )}

        {/* Clerk requires this element to be present when it calls
            signUp.update() — without it, bot-protection CAPTCHA never
            resolves and the submit just hangs. Hidden unless Clerk
            elects to use it. */}
        <div id="clerk-captcha" />

        <Button type="submit" className="w-full" disabled={submitting}>
          {submitting ? 'Joining…' : 'Join team'}
        </Button>
      </form>

      <p className="text-center text-sm text-[var(--color-mute)]">
        Wrong account?{' '}
        <button
          type="button"
          onClick={() => navigate({ to: '/sign-in' })}
          className="font-medium text-[var(--color-accent-brand)] hover:underline"
        >
          Sign in instead
        </button>
      </p>
    </div>
  );
}

function SignUpForm(): ReactElement {
  const { signUp, isLoaded } = useSignUp();
  const navigate = useNavigate();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [consent, setConsent] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string | undefined>>({});
  const [formError, setFormError] = useState<string | undefined>(undefined);

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!isLoaded || !signUp) return;

    if (!consent) {
      setFieldErrors({ consent: CONSENT_REQUIRED_MESSAGE });
      return;
    }

    setSubmitting(true);
    setFieldErrors({});
    setFormError(undefined);

    const result = await signUpWithPassword({
      signUp,
      emailAddress: email,
      password,
      firstName: firstName || undefined,
      lastName: lastName || undefined,
      legalAcceptance: buildLegalAcceptance('signup'),
    });

    if (result.kind === 'error') {
      setFieldErrors(result.fieldErrors);
      setFormError(result.formError);
      setSubmitting(false);
      return;
    }

    if (result.kind === 'needs_verification') {
      navigate({ to: '/verify-email' });
      return;
    }

    // Edge: Clerk returned `complete` without verification (unusual
    // for email+password but possible if the instance disables it).
    navigate({ to: '/onboarding' });
  }

  return (
    <div className="space-y-6">
      <header className="space-y-1 text-center">
        <h1 className="text-xl font-semibold text-[var(--color-ink)]">Create your account</h1>
        <p className="text-sm text-[var(--color-mute)]">
          Sign up for Clickweave. We'll email you a code to verify your address.
        </p>
      </header>

      <form className="space-y-4" onSubmit={onSubmit} noValidate>
        <div className="grid grid-cols-2 gap-3">
          <Field>
            <FieldLabel htmlFor="first-name">First name</FieldLabel>
            <Input
              id="first-name"
              autoComplete="given-name"
              value={firstName}
              onChange={(event) => setFirstName((event.target as HTMLInputElement).value)}
              aria-invalid={fieldErrors.firstName ? true : undefined}
            />
            {fieldErrors.firstName && <FieldError>{fieldErrors.firstName}</FieldError>}
          </Field>
          <Field>
            <FieldLabel htmlFor="last-name">Last name</FieldLabel>
            <Input
              id="last-name"
              autoComplete="family-name"
              value={lastName}
              onChange={(event) => setLastName((event.target as HTMLInputElement).value)}
              aria-invalid={fieldErrors.lastName ? true : undefined}
            />
            {fieldErrors.lastName && <FieldError>{fieldErrors.lastName}</FieldError>}
          </Field>
        </div>

        <Field>
          <FieldLabel htmlFor="email">Email</FieldLabel>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(event) => setEmail((event.target as HTMLInputElement).value)}
            aria-invalid={fieldErrors.emailAddress ? true : undefined}
          />
          {fieldErrors.emailAddress && <FieldError>{fieldErrors.emailAddress}</FieldError>}
        </Field>

        <Field>
          <FieldLabel htmlFor="password">Password</FieldLabel>
          <div className="relative w-full">
            <Input
              id="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              required
              minLength={8}
              value={password}
              onChange={(event) => setPassword((event.target as HTMLInputElement).value)}
              aria-invalid={fieldErrors.password ? true : undefined}
            />
            <button
              type="button"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              onClick={() => setShowPassword((prev) => !prev)}
              className="absolute inset-y-0 right-2 flex items-center text-[var(--color-mute)] hover:text-[var(--color-ink)]"
            >
              {showPassword ? (
                <EyeSlash aria-hidden="true" size={16} />
              ) : (
                <Eye aria-hidden="true" size={16} />
              )}
            </button>
          </div>
          {fieldErrors.password ? (
            <FieldError>{fieldErrors.password}</FieldError>
          ) : (
            <p className="text-xs text-[var(--color-mute)]">At least 8 characters.</p>
          )}
        </Field>

        {formError && (
          <div
            className="rounded-md border border-[var(--color-danger)]/30 bg-[var(--color-danger)]/10 px-3 py-2 text-sm text-[var(--color-danger)]"
            role="alert"
          >
            {formError}
          </div>
        )}

        <LegalConsentField
          checked={consent}
          onChange={(next) => {
            setConsent(next);
            if (next) setFieldErrors((prev) => ({ ...prev, consent: undefined }));
          }}
          error={fieldErrors.consent}
        />

        {/* Clerk requires a CAPTCHA element to be present on the page
            for bot protection. Hidden unless Clerk elects to use it. */}
        <div id="clerk-captcha" />

        <Button type="submit" className="w-full" disabled={submitting || !consent}>
          {submitting ? 'Creating account…' : 'Create account'}
        </Button>
      </form>

      <p className="text-center text-sm text-[var(--color-mute)]">
        Already have an account?{' '}
        <Link
          to="/sign-in"
          className="font-medium text-[var(--color-accent-brand)] hover:underline"
        >
          Sign in
        </Link>
      </p>
    </div>
  );
}
